import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!data.success) {
        if (data.errors) {
          const errorMessages = data.errors.map(err => err.message).join(', ');
          throw new Error(errorMessages);
        }
        throw new Error(data.message);
      }

      const { role } = data.data.user;
      if (role !== 'ADMIN' && role !== 'STAFF') {
        throw new Error('Invalid role. Access denied.');
      }

      login(data.data.user, data.data.token);
      
      if (role === 'ADMIN') {
        navigate('/admin');
      } else if (role === 'STAFF') {
        navigate('/ict-coor');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const appVersion = import.meta.env.VITE_VERSION;

  return (
    <div className="min-h-screen flex relative overflow-hidden"
         style={{
           backgroundImage: `url(${import.meta.env.BASE_URL}background.jpg)`,
           backgroundSize: 'cover',
           backgroundPosition: 'center',
           filter: 'brightness(0.7)'
         }}>
      
      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/70 to-black/40 z-10"></div>

      {/* Content Container */}
      <div className="w-full max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-center md:justify-between relative z-20 px-4 md:px-6 py-4 md:py-0">
        
        {/* Mobile Header for small screens */}
        <div className="md:hidden text-white text-center mb-8 animate-fade-in">
          <h1 className="text-2xl font-bold mb-2 font-poppins text-shadow-lg">
            <span className="text-blue-600">Dep</span><span className="text-red-600">ED</span> Imus City
          </h1>
          <p className="text-lg opacity-90 font-poppins text-shadow">
            SDO Ticketing Portal
          </p>
        </div>

        {/* Left side - Welcome Text (hidden on mobile) */}
        <div className="hidden md:block flex-1 text-white pr-8 animate-fade-in">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6 font-poppins text-shadow-lg relative">
            Welcome to <span className="text-blue-600">Dep</span><span className="text-red-600">Ed</span> Imus City
            <div className="absolute bottom-0 left-0 w-16 h-1 bg-gradient-to-r from-blue-500 to-transparent rounded"></div>
          </h1>
          <p className="text-lg lg:text-xl opacity-90 font-poppins text-shadow max-w-[80%] animate-fade-in-delay">
            Access the SDO Ticketing Portal.
          </p>
        </div>

        {/* Right side - Login Form */}
        <div className="w-full sm:w-[450px] max-w-[90%] sm:max-w-[450px] bg-white/92 p-6 sm:p-8 rounded-2xl backdrop-blur-lg shadow-2xl relative overflow-hidden animate-float-in border border-white/20">
          
          {/* Form gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/25 to-transparent z-0"></div>
          <div className="absolute -top-1/2 -right-1/2 w-48 h-48 bg-gradient-radial from-blue-100/20 to-transparent z-0"></div>
          
          <div className="relative z-10 flex flex-col items-center">
            
            {/* Logo */}
            <div className="flex justify-center mb-6 relative animate-pulse-slow">
              <img
                src={`${import.meta.env.BASE_URL}deped-logo.png`}
                alt="DepEd Logo"
                className="w-16 h-16 sm:w-20 sm:h-20 drop-shadow-lg transition-all duration-300 hover:scale-105 hover:drop-shadow-xl"
              />
            </div>

            <h2 className="text-xl sm:text-2xl font-extrabold mb-1 text-blue-800 tracking-wide uppercase font-poppins text-shadow-sm animate-fade-in-delay">
              SDO Portal
            </h2>
            <p className="mb-6 text-gray-600 font-medium tracking-wide text-sm font-poppins animate-fade-in-delay-2">
              Login
            </p>

            {error && (
              <div className="w-full mb-4 p-4 bg-red-50 border border-red-200 rounded-lg animate-fade-in shadow-lg">
                <p className="text-red-700 font-medium text-sm font-poppins">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="w-full animate-fade-in-delay-3">
              
              {/* Username Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                  Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-blue-600 drop-shadow-sm" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl bg-white/70 backdrop-blur-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:bg-white/85 hover:shadow-md font-poppins"
                    placeholder="Enter username"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-blue-600 drop-shadow-sm" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl bg-white/70 backdrop-blur-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:bg-white/85 hover:shadow-md font-poppins"
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-blue-600 hover:bg-blue-50 rounded-r-xl transition-colors duration-200"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                        <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full mb-4 py-3 sm:py-4 relative overflow-hidden bg-gradient-to-r from-blue-700 to-blue-900 text-white font-semibold text-sm sm:text-base rounded-xl shadow-lg font-poppins transition-all duration-300 hover:from-blue-800 hover:to-blue-900 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Logging in...
                  </div>
                ) : (
                  'Login'
                )}
              </button>

              {/* Return to Home Button */}
              <button
                type="button"
                onClick={() => navigate('/')}
                disabled={loading}
                className="w-full py-3 sm:py-4 relative overflow-hidden border border-blue-600 text-blue-600 font-medium text-sm sm:text-base rounded-xl bg-white/70 backdrop-blur-sm shadow-sm font-poppins transition-all duration-300 hover:bg-white/90 hover:shadow-md hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Return to Home
              </button>
            </form>

            {/* Bottom Logos */}
            <div className="flex justify-center gap-4 sm:gap-8 mt-6 sm:mt-8 pt-4 border-t border-gray-200 animate-fade-in-delay-4">
              <img
                src={`${import.meta.env.BASE_URL}logo-1.png`}
                alt="Logo 1"
                className="h-12 sm:h-16 w-auto drop-shadow-md transition-all duration-300 hover:scale-105 hover:drop-shadow-lg"
              />
              <img
                src={`${import.meta.env.BASE_URL}logo-2.png`}
                alt="Logo 2"
                className="h-12 sm:h-16 w-auto drop-shadow-md transition-all duration-300 hover:scale-105 hover:drop-shadow-lg"
              />
            </div>

            <p className="text-xs sm:text-sm font-poppins text-gray-400 mt-2 italic animate-fade-in-delay-5">
              version {appVersion}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 